/**
 * MessageInputWithEmojis
 *
 * Input de texto + selector de emojis estilo WhatsApp integrado.
 * Construido sobre el helper `EmojiPickerButton` para evitar duplicación.
 *
 * Comportamiento:
 *   - Botón "smile" anclado al extremo derecho del input (junto al contador).
 *   - Click → abre Popover con `<EmojiPicker>` (lazy-loaded).
 *   - Inserta el emoji en la posición exacta del cursor (selectionStart).
 *   - Respeta el límite `maxLength` (medido en code-points).
 *
 * Props:
 *   value, onChange(text), maxLength (default 90), placeholder, testId, className
 */
import { useRef } from "react";
import { Input } from "../components/ui/input";
import EmojiPickerButton, { insertEmojiAtCursor } from "./EmojiPickerButton";

export default function MessageInputWithEmojis({
  value = "",
  onChange,
  maxLength = 90,
  placeholder = "Escribe un mensaje corto...",
  testId = "message-input",
  className = "",
}) {
  const inputRef = useRef(null);

  const handleSelect = (emoji) => {
    const next = insertEmojiAtCursor(inputRef.current, value, emoji, maxLength);
    if (next != null) onChange(next);
  };

  const handleTextChange = (e) => {
    const v = e.target.value;
    if ([...v].length <= maxLength) onChange(v);
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleTextChange}
        data-testid={testId}
        className={`bg-transparent border-2 border-border rounded-xl h-12 text-white placeholder:text-zinc-600 focus:border-neon-pink pr-24 ${className}`}
      />

      {/* Counter */}
      <span
        className="absolute right-12 top-1/2 -translate-y-1/2 text-xs text-zinc-500 font-body tabular-nums select-none pointer-events-none"
        aria-live="polite"
      >
        {[...value].length}/{maxLength}
      </span>

      {/* Emoji button + popover */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2">
        <EmojiPickerButton
          onSelect={handleSelect}
          testId={`${testId}-emoji-trigger`}
        />
      </div>
    </div>
  );
}
